import React, { forwardRef } from 'react';
import { cx } from '@linaria/atomic';
import { motion, AnimatePresence, type MotionProps } from 'motion/react';
import type { CommonHTMLAttributes } from '@frontend/types';
import type { FontColorType } from '../../helpers/font-styles';
import { ConfirmBadgeIcon } from '../../icon/icons/confirm-badge';
import { ErrorBadgeIcon } from '../../icon/icons/error-badge';
import { InfoBadgeIcon } from '../../icon/icons/info-badge';
import { WarningBadgeIcon } from '../../icon/icons/warning-badge';
import { AlertType } from '../alerts/types';
import { Text } from '../text/text.component';
import { alertContainerStyle, getAlertTextClassName } from './alert.styles';

type AlertProps = Omit<CommonHTMLAttributes, 'onClick'> &
  MotionProps & {
    children: React.ReactNode;
    id?: string;
    onClick?: (id: string) => void;
    type: AlertType;
  };

const iconMap = {
  error: ErrorBadgeIcon,
  info: InfoBadgeIcon,
  success: ConfirmBadgeIcon,
  warning: WarningBadgeIcon,
};

const iconColorMap: { [key: string]: FontColorType } = {
  error: 'error',
  info: 'primary',
  success: 'success',
  warning: 'warn',
};

export const Alert = forwardRef<HTMLParagraphElement, AlertProps>(
  ({ children, id, onClick, type, className, ...rest }, ref) => {
    const Icon = iconMap[type];
    let handleClick;
    if (typeof onClick === 'function') {
      handleClick = () => onClick(id ?? '');
    }
    return (
      <AnimatePresence>
        <motion.div
          id={id}
          className={cx(alertContainerStyle, className)}
          {...rest}
          aria-live={type === 'error' || type === 'warning' ? 'assertive' : 'polite'}
          onClick={handleClick}
          ref={ref}
        >
          <Text size='medium' className={getAlertTextClassName(type)}>
            <Icon color={iconColorMap[type]} />
            {children}
          </Text>
        </motion.div>
      </AnimatePresence>
    );
  }
);
Alert.displayName = 'Alert';
